import React from 'react'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useAppSelector } from '../../../store/hooks'
import { selectEmailThreads, selectEmailThreadsLoading, selectEmailThreadsMoreLoading } from '../../../store/emailHub/emailThreads'
import { TableColumn, TableStyles } from 'react-data-table-component'
import { formatDateDDMM, formatDateDDMMYYYY, formatDateHM } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { IntegrationEmailThread } from '../../../models/Email'
import { EmailStatusCell } from './EmailStatusCell'
import { EmailFromCell } from './EmailFromCell'
import { EmailTypeCell } from './EmailTypeCell'
import './EmailTable.scss'
import { EmailTableLoadMoreButton } from './EmailTableLoadMoreButton'

const TABLE_STYLES: TableStyles = {
  rows: {
    style: {
      borderBottomColor: '#E9ECEF !important',
      cursor: 'pointer',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#F8F9FA',
      borderBottomColor: '#F8F9FA !important',
    },
  },
}

export interface EmailTableProps {
  onRowClicked: (thread: IntegrationEmailThread) => void
}

export const EmailTable: React.FC<EmailTableProps> = ({ onRowClicked }) => {
  const emailsThreadsLoading = useAppSelector(selectEmailThreadsLoading)
  const emailsThreadsMoreLoading = useAppSelector(selectEmailThreadsMoreLoading)
  const emailThreads = useAppSelector(selectEmailThreads)

  const handleRowClicked = onRowClicked

  const columns: TableColumn<IntegrationEmailThread>[] = [
    {
      name: 'From',
      grow: 2,
      cell: (row) => <EmailFromCell thread={row} onClick={handleRowClicked} />,
    },
    {
      name: 'Subject',
      wrap: false,
      grow: 8,
      cell: (row) => (
        <div className='d-flex subject-container' onClick={() => handleRowClicked(row)}>
          <span className='paragraph-medium-extended subject'>{row.emails[0].subject || '(no subject)'}</span>
          <span className='px-1 paragraph-medium-extended zocks-text-gray-500 snippet'>{' - ' + (row.emails[0].snippet || '')}</span>
        </div>
      ),
      selector: (row) => row.emails[0].subject || '(no subject)',
    },
    {
      name: 'Received at',
      grow: 1,
      cell: (row) => {
        const lastEmail = row.emails.at(-1)
        if (!lastEmail) {
          return null
        }
        const createdAt = lastEmail.createdAt
        const createdAtDate = new Date(createdAt)
        const now = new Date()
        const isToday = createdAtDate.toDateString() === now.toDateString()
        const isSameYear = createdAtDate.getFullYear() === now.getFullYear()
        const formatter = isToday ? formatDateHM : isSameYear ? formatDateDDMM : formatDateDDMMYYYY
        return <div onClick={() => handleRowClicked(row)}>{formatter(createdAt)}</div>
      },
    },
    {
      name: 'Type',
      grow: 2,
      cell: (row) => <EmailTypeCell thread={row} />,
    },
    {
      name: 'Status',
      grow: 2,
      cell: (row) => <EmailStatusCell thread={row} onClick={handleRowClicked} />,
    },
  ]

  return (
    <div className='d-flex flex-column'>
      {emailsThreadsLoading && !emailsThreadsMoreLoading && !emailThreads.length ? (
        <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />
      ) : null}
      <ZocksDataTable
        columns={columns}
        data={emailThreads}
        pagination={false}
        noDataComponent={null}
        customStyles={TABLE_STYLES}
        onRowClicked={handleRowClicked}
      />
      <EmailTableLoadMoreButton />
      {!emailsThreadsLoading && !emailThreads.length ? <div className='p-3'>No emails found. Try extending your search range.</div> : null}
    </div>
  )
}
